// src/pages/Vendor/Dashboard/index.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  Package,
  ShoppingBag,
  DollarSign,
  Star,
  ArrowUpRight,
  BarChart3,
  Store,
  Settings,
  RotateCcw
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { formatCurrency } from '../../../utils/currencyFormatter';
import { formatDateEnglish } from '../../../utils/date';
import type { VendorDashboardStats } from '../../../types';

const VendorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { stats, statsLoading } = useOutletContext<{
    stats: VendorDashboardStats | null;
    statsLoading: boolean;
  }>();

  if (statsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center py-12">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">No Data Available</h2>
          <p className="text-gray-500">Start adding products to see your dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Vendor Dashboard</h1>
            <p className="text-sm text-gray-500">Welcome back, {user?.first_name}</p>
          </div>
          <div className="flex items-center gap-3 mt-3 sm:mt-0">
            <button
              onClick={() => navigate('/vendor/products/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
            >
              <Package className="h-4 w-4" />
              Add Product
            </button>
            <button
              onClick={() => navigate('/vendor/settings')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Settings className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Products"
            value={stats.total_products}
            icon={Package}
            color="bg-blue-500"
            trend={`+12% this month`}
          />
          <StatCard
            title="Orders"
            value={stats.total_orders}
            icon={ShoppingBag}
            color="bg-green-500"
            trend={`${stats.pending_orders ?? '0'} pending`}
          />
          <StatCard
            title="Revenue"
            value={`${formatCurrency(stats.total_revenue)}`}
            icon={DollarSign}
            color="bg-purple-500"
            trend={`${stats.total_sales} sales`}
          />
          <StatCard
            title="Rating"
            value={0}
            icon={Star}
            color="bg-yellow-500"
            trend="★ 4.8 average"
          />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <QuickAction
            title="Manage Products"
            icon={Package}
            onClick={() => navigate('/vendor/products')}
          />
          <QuickAction
            title="View Orders"
            icon={ShoppingBag}
            onClick={() => navigate('/vendor/orders')}
            badge={stats.pending_orders}
          />
          <QuickAction
            title="Analytics"
            icon={BarChart3}
            onClick={() => navigate('/vendor/analytics')}
          />
          <QuickAction
            title="Store Settings"
            icon={Store}
            onClick={() => navigate('/vendor/settings')}
          />
          <QuickAction
            title="Returns"
            icon={RotateCcw}
            onClick={() => navigate('/vendor/returns')}
            badge={stats.pending_returns}
          />
        </div>

        {/* Recent Orders / Returns / Top Products */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Orders */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Recent Orders</h3>
              <button
                onClick={() => navigate('/vendor/orders')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                View all
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>

            {stats.recent_orders.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No orders yet</p>
            ) : (
              <div className="space-y-3">
                {stats.recent_orders.map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                    onClick={() => navigate(`/vendor/orders/${order.id}`)}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">#{order.order_number}</p>
                      <p className="text-xs text-gray-500">{order.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(order.total)}</p>
                      <p className="text-xs text-gray-500">{formatDateEnglish(order.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Returns */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">Returns</h3>
                {stats.pending_returns > 0 && (
                  <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full font-medium">
                    {stats.pending_returns}
                  </span>
                )}
              </div>
              <button
                onClick={() => navigate('/vendor/returns')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                View all
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>

            {stats.recent_returns.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <RotateCcw className="h-8 w-8 text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No returns awaiting action</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.recent_returns.map(r => (
                  <div
                    key={r.id}
                    onClick={() => navigate(`/vendor/returns/${r.id}`)}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      {r.product_image ? (
                        <img src={r.product_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Package className="w-full h-full p-1.5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {r.product_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        #{r.order_number} · {r.customer_name}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium text-gray-900">
                        {formatCurrency(r.item_refund_amount)}
                      </p>
                      <p className="text-[10px] text-amber-600">
                        due {formatDateEnglish(r.sla_deadline)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Top Products */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Top Products</h3>
              <button
                onClick={() => navigate('/vendor/products')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
              >
                View all
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </div>

            {stats.top_products.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No products yet</p>
            ) : (
              <div className="space-y-3">
                {stats.top_products.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                    onClick={() => navigate(`/vendor/products/${product.id}`)}
                  >
                    <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package className="w-full h-full p-2 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                      <p className="text-xs text-gray-500">{product.sales} sales</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(product.revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  trend: string;
}> = ({ title, value, icon: Icon, color, trend }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm text-gray-500">{title}</span>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="h-4 w-4 text-white" />
      </div>
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500 mt-1">{trend}</p>
  </div>
);

// Quick Action Component
const QuickAction: React.FC<{
  title: string;
  icon: React.ElementType;
  onClick: () => void;
  badge?: number;
}> = ({ title, icon: Icon, onClick, badge }) => (
  <button
    onClick={onClick}
    className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow text-left relative"
  >
    <Icon className="h-5 w-5 text-gray-700 mb-2" />
    <p className="text-sm font-medium text-gray-900">{title}</p>
    {badge !== undefined && badge > 0 && (
      <span className="absolute top-2 right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
        {badge}
      </span>
    )}
  </button>
);

export default VendorDashboard;